import React from 'react';

/**
 * A small bar that displays a collection of relics (a.k.a. trinkets) with
 * simple hover/tap tooltips. This component is intentionally
 * lightweight and uses inline styles rather than depending on any
 * external animation library. It roughly replicates the relic display
 * found in the dungeon exploration UI for both desktop and mobile
 * layouts.
 */
export type Relic = {
  id: string;
  name: string;
  description: string;
  image: string;
  rarity?: string;
};

// Props for the RelicBar component. Providing an explicit type allows
// us to write a custom comparator that avoids re-rendering the bar when
// its references stay the same. The relics array, pulseMap and feedbackMap
// are compared by reference only; if they are new objects but with
// identical contents, callers should memoize them to benefit from this.
export type RelicBarProps = {
  relics?: Relic[];
  pulseMap?: Record<string, number>;
  feedbackMap?: Record<string, RelicFeedbackBadge[]>;
};

/**
 * Compare props for the RelicBar. To avoid re-rendering on every Board
 * animation tick, we compare incoming references directly. When callers
 * provide stable references for relics, pulseMap and feedbackMap, the
 * bar will only update when these references actually change.
 */
function areRelicBarPropsEqual(prev: RelicBarProps, next: RelicBarProps) {
  return (
    prev.relics === next.relics &&
    prev.pulseMap === next.pulseMap &&
    prev.feedbackMap === next.feedbackMap
  );
}

type RelicIconProps = {
  key?: string;
  relic: Relic;
  pulseTick?: number;
  feedback?: RelicFeedbackBadge[];
  isMobile: boolean;
  onDesktopEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDesktopMove?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDesktopLeave?: () => void;
  onMobileClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type RelicFeedbackBadge = {
  text: string;
  tick: number;
};

function RelicIcon({ relic, pulseTick, feedback, isMobile, onDesktopEnter, onDesktopMove, onDesktopLeave, onMobileClick }: RelicIconProps) {
  const [isPulsing, setIsPulsing] = React.useState(false);

  React.useEffect(() => {
    if (pulseTick == null) return;
    setIsPulsing(false);
    let timeoutId: number | null = null;
    const raf = requestAnimationFrame(() => {
      setIsPulsing(true);
      timeoutId = window.setTimeout(() => {
        setIsPulsing(false);
      }, 3150);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [pulseTick]);

  const imgStyle: React.CSSProperties = isPulsing
    ? {
        filter: 'drop-shadow(0 0 8px rgba(255,220,120,0.95)) drop-shadow(0 0 22px rgba(255,220,120,0.55))',
        transform: 'scale(1.1)',
      }
    : {};

  if (!isMobile) {
    return (
      <div
        onMouseEnter={onDesktopEnter}
        onMouseMove={onDesktopMove}
        onMouseLeave={onDesktopLeave}
        className={isPulsing ? 'relicPulse' : undefined}
        style={{ cursor: 'pointer', marginBottom: '8px', position: 'relative', overflow: 'visible' }}
      >
        <img
          src={relic.image}
          alt={relic.name}
          style={{ width: 38, height: 38, objectFit: 'contain', pointerEvents: 'none', ...imgStyle }}
        />
        {Array.isArray(feedback) && feedback.length > 0 ? (
          <span
            className="relicFeedbackStack"
            style={{
              display: 'inline-flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              width: 'max-content',
            }}
          >
            {feedback.map((entry) => (
              <span
                key={`relic-feedback-${relic.id}-${entry.tick}`}
                className="relicFeedbackText"
                style={{ display: 'inline-flex', flex: '0 0 auto' }}
              >
                {entry.text}
              </span>
            ))}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onMobileClick}
      className={isPulsing ? 'relicPulse' : undefined}
      style={{
        padding: 0,
        margin: 0,
        background: 'transparent',
        border: 'none',
        display: 'block',
        marginBottom: '8px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <img
        src={relic.image}
        alt={relic.name}
        style={{ width: 32, height: 32, objectFit: 'contain', pointerEvents: 'none', ...imgStyle }}
      />
      {Array.isArray(feedback) && feedback.length > 0 ? (
        <span
          className="relicFeedbackStack"
          style={{
            display: 'inline-flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            width: 'max-content',
          }}
        >
          {feedback.map((entry) => (
            <span
              key={`relic-feedback-${relic.id}-${entry.tick}`}
              className="relicFeedbackText"
              style={{ display: 'inline-flex', flex: '0 0 auto' }}
            >
              {entry.text}
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}

function RelicBar({
  relics,
  pulseMap = {},
  feedbackMap = {},
}: RelicBarProps) {
  const [hoveredRelic, setHoveredRelic] = React.useState<Relic | null>(null);
  const [hoverY, setHoverY] = React.useState<number>(0);
  const [mobileRelicInfo, setMobileRelicInfo] = React.useState<Relic | null>(null);
  const [isMobile, setIsMobile] = React.useState<boolean>(false);

  React.useEffect(() => {
    const update = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 768);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  if (!relics || relics.length === 0) return null;

  const displayed = relics;

  const Tooltip = ({ relic }: { relic: Relic }) => (
    <div
      style={{
        background: 'rgba(0,0,0,0.8)',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #eab308',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '360px',
        pointerEvents: 'auto',
      }}
    >
      <img src={relic.image} alt={relic.name} style={{ width: 48, height: 48, objectFit: 'contain', marginRight: 12 }} />
      <div>
        <div style={{ fontWeight: 700, color: '#facc15' }}>{relic.name}</div>
        {relic.rarity && (
          <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.7, color: '#e5e5e5' }}>
            {relic.rarity}
          </div>
        )}
        <div style={{ fontSize: 14, color: '#d1d5db' }}>{relic.description}</div>
      </div>
    </div>
  );

  return (
    <>
      {!isMobile && hoveredRelic && (
        <div
          style={{
            position: 'fixed',
            left: '64px',
            top: Math.max(90, Math.min(window.innerHeight - 90, hoverY || window.innerHeight / 2)),
            transform: 'translateY(-50%)',
            zIndex: 40,
          }}
        >
          <Tooltip relic={hoveredRelic} />
        </div>
      )}

      {isMobile && mobileRelicInfo && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: '110px',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <Tooltip relic={mobileRelicInfo} />
        </div>
      )}

      {!isMobile && (
        <div
          style={{
            position: 'fixed',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 30,
            pointerEvents: 'auto',
          }}
        >
          {displayed.map((r) => (
            <RelicIcon
              key={r.id}
              relic={r}
              pulseTick={pulseMap[r.id]}
              feedback={feedbackMap[r.id]}
              isMobile={false}
              onDesktopEnter={(e) => {
                setHoveredRelic(r);
                setHoverY((e as any).clientY || 0);
              }}
              onDesktopMove={(e) => setHoverY((e as any).clientY || 0)}
              onDesktopLeave={() => setHoveredRelic(null)}
            />
          ))}
        </div>
      )}

      {isMobile && (
        <div
          style={{
            position: 'fixed',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 30,
            pointerEvents: 'auto',
          }}
        >
          {displayed.map((r) => (
            <RelicIcon
              key={r.id}
              relic={r}
              pulseTick={pulseMap[r.id]}
              feedback={feedbackMap[r.id]}
              isMobile
              onMobileClick={(e) => {
                e.stopPropagation();
                setMobileRelicInfo((cur) => (cur && cur.id === r.id ? null : r));
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Memoize RelicBar so that it does not re-render unless the relics array or
// its auxiliary maps actually change. This is particularly important because
// Board's local animation state updates frequently but does not affect the
// relic bar contents.
export default React.memo(RelicBar, areRelicBarPropsEqual);
